// @flow
import React from 'react';
import { includes, get } from 'lodash';
import classNames from 'classnames';
import { isPresent, shortenName, handleMomenti18n } from '@helpers/helpers';
import { Icon, Input, Checkbox, FormattedText } from '@elements';
import { ServiceTemplatePrice } from '@blocks';
import moment from 'moment';
import { highlightMatch, plainWords } from './highlightMatch';

// TODO: Handle key's

// The appointment's day as people say it: "Qui, 29 de outubro" (the year only when it isn't this one), with
// Hoje / Amanhã / Ontem next to it, in the app's language
const relativeDay = (offset: number, _: Function): ?string => {
  switch (offset) {
    case -1: return _('label/date/yesterday');
    case 0: return _('label/calendar_today');
    case 1: return _('label/date/tomorrow');
    default: return null;
  }
};

export const dayPickerInputRenderer = (_: Function, i18n: Object) => (date: any, callback: Function) => {
  const now = moment();
  const day = moment(date, 'DD/MM/YYYY');
  handleMomenti18n(i18n, day);
  // Spanish abbreviations end in a period ("jue."): not before the comma
  const weekday = day.format('ddd').replace(/\.$/, '');
  const label = `${weekday}, ${day.format(day.isSame(now, 'year') ? 'D [de] MMMM' : 'D [de] MMMM [de] YYYY')}`;
  const relative = relativeDay(day.clone().startOf('day').diff(now.clone().startOf('day'), 'days'), _);
  return (
    <button type="button" className="calendarTrigger btn" onClick={callback} aria-haspopup="dialog" aria-label={relative ? `${label}, ${relative}` : label}>
      <Icon iconName="calendar" className="icon" size={16} aria-hidden="true" />
      <span className="dateLabel">{label}</span>
      {relative && <span className="dateRelative">{relative}</span>}
      <Icon iconName="edit" className="icon" size={16} aria-hidden="true" />
    </button>
  );
};

export const appointmentModalListRenderer = (errors: Array<string>, handleFormChange: Function, notes: any, showNotes: boolean, title: any) => (item: Object) => {
  if (item.type === 'notesField') {
    return ([
      <Icon
        className="icon"
        key="leftModalInputIcon"
        iconName={item.input.icon}
        size={24}
      />,
      <Input
        key="leftModalInputPlaceholder"
        type="text"
        name="notes"
        placeholder={item.input.placeholder}
        onChange={handleFormChange}
        value={notes}
        disabled={item.disabled}
      />,
      isPresent(notes) && (
      <div key="leftModalCheckbox" id="notesOnRegistry">
        <Checkbox
          name="showNotes"
          label={item.checkbox.label}
          onChange={handleFormChange}
          isChecked={showNotes}
          disabled={item.disabled}
        />
      </div>
      ),
    ]);
  }

  if (item.type === 'titleField') {
    return ([
      <Icon
        className="icon"
        key="leftModalInputIcon"
        iconName={item.input.icon}
        size={24}
      />,
      <Input
        key="leftModalInputPlaceholder"
        type="text"
        name="title"
        placeholder={item.input.placeholder}
        onChange={handleFormChange}
        value={title}
        disabled={item.disabled}
      />,
    ]);
  }

  const isValid = !item.validate || (item.validate && !includes(errors, item.validate));

  // The row's value (the field's name in grey while empty), and under it the patient's phone, the services' total or
  // when the status changed
  const textClassName = classNames({ error: !isValid, placeholder: item.placeholder && isValid });
  return ([
    <span className={classNames('appointmentModalText', { disabled: item.disabled })} key={`appointmentModalText${item.id}${item.date}`}>
      <Icon iconName={item.icon} className={classNames('icon', item.iconClassName)} size={24} />
      <span className={textClassName || null}>
        {item.text}
        {item.phone && (
          <div className="appointmentPatientItemPhone">
            <FormattedText type="phone" text={item.phone} />
          </div>
        )}
        {item.total !== null && item.total !== undefined && (
          <div className="appointmentItemDetail">
            <FormattedText type="currency" text={item.total} />
          </div>
        )}
        {item.detail && <div className="appointmentItemDetail">{item.detail}</div>}
      </span>

    </span>,
    <Icon key="appointmentModaIcon" iconName="arrow-right" className="icon" />,
  ]);
};

export const newPatientRenderer = (item: Object) => ([
  <Icon key="patientSecondaryModalIcon" iconName={item.icon} className="icon" size={20} />,
  <span key="patientSecondaryModalText">{item.text}</span>,
]);

// A patient in the picker: the name, with what was typed in bold, and their professional under it
export const patientsListRenderer = (typed: string) => {
  const words = plainWords(typed);
  return (item: Object) => {
    const doctorName = shortenName(get(item, ['doctor', 'nameWithTitle'], ''));
    return [
      <span key="patientSecondaryModalPatient" className="patientPick">
        <span className="patientName">
          {highlightMatch(shortenName(item.patient.name), words).map((piece) => (
            piece.match ? <strong key={`m${piece.at}`}>{piece.text}</strong> : <React.Fragment key={`t${piece.at}`}>{piece.text}</React.Fragment>
          ))}
        </span>
        {doctorName && <span className="patientDoctor">{doctorName}</span>}
      </span>,
    ];
  };
};

export const servicesRenderer = (selectedServices: any, handleServicesClick: Function) => (item: Object) => (
  <div className='editCheckboxesWrapper servicePriceCheckboxWrapper' onClick={handleServicesClick(item.service.id)} key={item.key}>
    <div className="labelBox">
      <Checkbox
        className={includes(selectedServices, item.service.id) ? 'force-checked' : 'force-unchecked'}
        key={`${item.type}-${item.service.id}`}
        label={item.service.name}
        isChecked={includes(selectedServices, item.service.id)}
      />
    </div>
    <div className="priceBox">
      <span className='price piceLabel'>
        <ServiceTemplatePrice template={item.service} />
      </span>
    </div>
  </div>
);

export const storedServicesRenderer = (handleServicesClick: Function) => (item: Object) => (
  <div className='editCheckboxesWrapper servicePriceCheckboxWrapper' onClick={handleServicesClick(item.service.id)} key={item.key}>
    <div className="labelBox">
      <Checkbox
        className="force-checked"
        label={item.service.name}
        disabled={!isPresent(get(item, ['service', 'id']))}
        isChecked
      />
    </div>
    <div className="priceBox">
      <span className='price piceLabel'>
        <FormattedText type="currency" text={item.service.price} />
      </span>
    </div>
  </div>
);
